import { analyticsAPI } from './AnalyticsAPI';
import type {
    InteractionEntity,
    InteractionEventName,
    InteractionInfo,
    InteractionOperation,
    PanelEntity,
    PanelEventName,
    PanelOperation,
    TrackingEventName,
} from '@publicala/delfino';

/**
 * Tracks reader interaction events (notes, highlights and flashcards CRUD) so
 * Coniglio can count engagement with the study/annotation tools per session.
 *
 * Emits only the *fact* of the action — the event name (`{entity}-{operation}`)
 * plus the backend-assigned entity id (and, for a print job, how many pages it
 * printed) — never the note/highlight/flashcard text.
 * The host attributes the event to the current session (issue, user, tenant)
 * from the session context.
 *
 * Fire-and-forget by design: every emit swallows its own errors and is not
 * awaited by callers, so a tracking failure can never block or break the CRUD
 * operation that triggered it.
 */
class InteractionEventsTracker {
    /**
     * Emits a single interaction event. The event name is composed from the
     * entity and operation, matching the `InteractionEventName` matrix declared
     * in @publicala/delfino — so adding a new tracked entity there needs no
     * change here.
     *
     * @param entity - The interaction entity (note / highlight / flashcard).
     * @param operation - The CRUD operation (create / edit / delete).
     * @param id - Backend-assigned id of the affected entity, sent as a reference.
     *   A nullish id (e.g. an entity that was never persisted) is a no-op:
     *   without a backend id there is nothing for Coniglio to correlate.
     */
    track(
        entity: InteractionEntity,
        operation: InteractionOperation,
        id: InteractionInfo['id'] | null | undefined
    ): void {
        if (id == null) return;

        const eventName: InteractionEventName = `${entity}-${operation}`;
        this.emit(eventName, { id });
    }

    /**
     * Emits `print-create` with how many pages the job printed, so Coniglio
     * counts printed pages rather than print jobs.
     *
     * @param jobId - Server-assigned print job id.
     * @param pages - Pages in the printed range, both ends included.
     */
    trackPrint(jobId: InteractionInfo['id'], pages: number): void {
        this.emit('print-create', { id: jobId, pages });
    }

    /**
     * Emits a panel event (`{entity}-{operation}`, e.g. `plugin-open`), with
     * the panel's subject as the reference: for a plugin, its id.
     *
     * @param entity - The panel entity (plugin).
     * @param operation - open / close.
     * @param id - What the panel shows, e.g. the plugin id (`vito`, `external`).
     */
    trackPanel(entity: PanelEntity, operation: PanelOperation, id: InteractionInfo['id']): void {
        const eventName: PanelEventName = `${entity}-${operation}`;

        this.emit(eventName, { id });
    }

    /**
     * Sends an event without letting it fail the caller: the returned promise
     * is not awaited and its rejection is swallowed.
     *
     * @param eventName - The tracked event.
     * @param interaction - What the event refers to.
     */
    private emit(eventName: TrackingEventName, interaction: InteractionInfo): void {
        void analyticsAPI.track(eventName, { extraPayload: { interaction } }).catch(() => {
            // Best-effort: tracking must never surface to the user or fail the action that triggered it.
        });
    }
}

export const interactionEventsTracker = new InteractionEventsTracker();
